import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link2, ClipboardPaste, Loader2, Sparkles } from "lucide-react";
import { RETAILERS } from "@/lib/retailers";
import { verifyLink, getDailySpotlight, getGuidedSearchStatus } from "@/lib/api";
import { getTrustBadge, guessManufacturer } from "@/features/snaggedify/dealFeedData";
import { SafeImg } from "@/components/SafeImg";

const PRESETS = [
  { label: "Find an iPhone", query: "iphone 17 pro", retailer: "amazon" },
  { label: "Find a Laptop", query: "gaming laptop", retailer: "bestbuy" },
  { label: "Find Sneakers", query: "nike air jordan 1", retailer: "stockx" },
  { label: "Find a Console", query: "ps5 slim", retailer: "walmart" },
];

// Authenticated/Guest Home — Section 1. Replaces the old public infinite Scroll feed.
// Guided, intentional discovery: paste-a-link check, one Daily Spotlight (costs at
// most 1 live search credit total per day, across ALL users), preset shortcuts, and
// the retailer grid that leads into the strictly-scoped 3-Option Intent Engine.
export default function Dashboard({ active, onOpenPdp }) {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [spotlight, setSpotlight] = useState(null);
  const [credit, setCredit] = useState(null);

  useEffect(() => {
    if (!active) return;
    getDailySpotlight().then((r) => setSpotlight(r.deal)).catch(() => setSpotlight(null));
    getGuidedSearchStatus().then(setCredit).catch(() => setCredit(null));
  }, [active]);

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) setUrl(text);
    } catch { /* clipboard permission denied — ignore */ }
  };

  const submitCheck = async (e) => {
    e.preventDefault();
    if (!url.trim() || loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await verifyLink(url.trim());
      const r = res.inspection_result;
      onOpenPdp?.({
        id: r.domain, title: r.product_title, price: r.scraped_price ?? 0, msrp: r.msrp_baseline ?? r.scraped_price ?? 0,
        image: r.product_image, reseller: r.domain, manufacturer: guessManufacturer(r.product_title || ""),
        verdict: (r.verdict_code || "").replace("VERDICT_LEGIT", "VERIFIED_LEGIT").replace("VERDICT_", ""),
        outboundUrl: r.target_url, fulfillment: r.fulfillment_mode || "ONLINE_SHIPPING",
      });
      setUrl("");
    } catch (err) {
      setError(err?.message || "Couldn't inspect that link — try again.");
    } finally {
      setLoading(false);
    }
  };

  const openSpotlight = () => {
    if (!spotlight) return;
    onOpenPdp?.({
      id: spotlight.id, title: spotlight.title, price: spotlight.price, msrp: spotlight.msrp_baseline ?? spotlight.price,
      image: spotlight.image || "", reseller: spotlight.reseller, manufacturer: guessManufacturer(spotlight.title || ""),
      verdict: (spotlight.verdict || "").replace("VERDICT_LEGIT", "VERIFIED_LEGIT").replace("VERDICT_", ""),
      outboundUrl: spotlight.outbound_url, fulfillment: spotlight.fulfillment_mode || "ONLINE_SHIPPING",
    });
  };

  const spotlightBadge = spotlight ? getTrustBadge((spotlight.verdict || "").replace("VERDICT_LEGIT", "VERIFIED_LEGIT").replace("VERDICT_", "")) : null;

  return (
    <section id="view-home" data-testid="view-home" className={active ? "tab-view active" : "tab-view hidden"}>
      <form onSubmit={submitCheck} className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 dark:border-white/10 dark:bg-white/5" data-testid="dashboard-check-form">
        <Link2 className="h-4 w-4 shrink-0 text-slate-400" />
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Paste any reseller link to verify it — always free"
          data-testid="dashboard-check-input"
          className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-slate-400 dark:text-white dark:placeholder:text-zinc-500"
        />
        <button type="button" onClick={pasteFromClipboard} data-testid="dashboard-check-paste-btn" aria-label="Paste from clipboard" className="shrink-0 rounded-lg border border-slate-300 p-2 text-slate-500 dark:border-white/10 dark:text-zinc-400">
          <ClipboardPaste className="h-4 w-4" />
        </button>
        <button type="submit" disabled={!url.trim() || loading} data-testid="dashboard-check-submit" className="shrink-0 rounded-lg bg-slate-900 px-4 py-2 text-xs font-black uppercase tracking-wide text-white disabled:opacity-40 dark:bg-white dark:text-slate-950">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check Link"}
        </button>
      </form>
      {error && <p className="mt-2 text-sm font-bold text-rose-500" data-testid="dashboard-check-error">{error}</p>}

      {spotlight && (
        <button onClick={openSpotlight} data-testid="dashboard-spotlight-card" className="mt-4 flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 text-left dark:border-white/10 dark:bg-white/5">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-slate-100 dark:bg-white/10">
            {spotlight.image ? <SafeImg src={spotlight.image} alt={spotlight.title} className="h-full w-full object-cover" iconClassName="h-5 w-5 text-slate-300" /> : <Sparkles className="h-6 w-6 text-slate-300" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wide text-brand-orange dark:text-amber-400">
              <Sparkles className="h-3 w-3" /> Today's Spotlight{spotlight.is_demo ? " · Demo" : ""}
            </p>
            <p className="truncate text-sm font-bold text-slate-900 dark:text-white" data-testid="dashboard-spotlight-title">{spotlight.title}</p>
            <p className="text-xs font-bold text-slate-500 dark:text-zinc-400">${Number(spotlight.price || 0).toFixed(2)} · {spotlight.reseller}</p>
          </div>
          {spotlightBadge && <span className={`shrink-0 rounded-full px-2 py-1 text-[9px] font-black uppercase ${spotlightBadge.className}`}>{spotlightBadge.label}</span>}
        </button>
      )}

      <div className="mt-5 flex flex-wrap gap-2" data-testid="dashboard-presets">
        {PRESETS.map((p) => (
          <button
            key={p.label}
            onClick={() => navigate(`/search/${p.retailer}?q=${encodeURIComponent(p.query)}`)}
            data-testid={`dashboard-preset-${p.retailer}`}
            className="rounded-full border border-slate-300 px-3.5 py-1.5 text-xs font-bold text-slate-600 hover:border-brand-orange hover:text-brand-orange dark:border-white/10 dark:text-zinc-300"
          >
            {p.label}
          </button>
        ))}
      </div>

      <p className="mt-6 text-[10px] font-black uppercase tracking-wide text-slate-400" data-testid="dashboard-retailers-label">
        Search a Trusted Retailer
        {credit && credit.limit != null && <span className="ml-1 text-slate-400">· {credit.remaining} of {credit.limit} left today</span>}
      </p>
      <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="dashboard-retailer-grid">
        {RETAILERS.map((r) => (
          <button
            key={r.id}
            onClick={() => navigate(`/search/${r.id}`)}
            data-testid={`dashboard-retailer-${r.id}`}
            style={{ backgroundColor: r.primary, borderColor: r.accent }}
            className="flex h-16 flex-col items-center justify-center rounded-2xl border-b-4 px-2 transition-transform hover:scale-[1.03]"
          >
            <span className="text-center text-xs font-black leading-tight text-white drop-shadow">{r.name}</span>
          </button>
        ))}
      </div>
    </section>
  );
}
